import React, { useState, useEffect } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import './CheckinSummary.css';

// Cliff-notes digest of today's attendance check-in questionnaires for coaches/admins.
function CheckinSummary() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`${API_BASE_URL}/api/checkin/responses`)
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || rows.length === 0) return null;

  // Aggregate answers across athletes, keyed by question
  const scale = {}, yn = {}, text = {};
  rows.forEach(r => {
    (r.answers || []).forEach(a => {
      if (a.qtype === 'scale' || a.qtype === 'faces') {
        const v = Number(a.response);
        const s = scale[a.question_id] = scale[a.question_id] || { prompt: a.prompt, sum: 0, count: 0, high: [] };
        if (!Number.isNaN(v) && a.response !== null && a.response !== '') {
          s.sum += v; s.count++;
          if (v >= 4) s.high.push(r.athlete_name);
        }
      } else if (a.qtype === 'yesno') {
        const s = yn[a.question_id] = yn[a.question_id] || { prompt: a.prompt, yes: [], no: 0 };
        if (a.response === 'Yes') s.yes.push(r.athlete_name); else if (a.response === 'No') s.no++;
      } else {
        const s = text[a.question_id] = text[a.question_id] || { prompt: a.prompt, items: [] };
        if (a.response) s.items.push({ name: r.athlete_name, response: a.response });
      }
    });
  });

  // "Worth a look": anyone at 4+ on a scale or who answered Yes on a yes/no
  const flagged = new Set();
  Object.values(scale).forEach(s => s.high.forEach(n => flagged.add(n)));
  Object.values(yn).forEach(s => s.yes.forEach(n => flagged.add(n)));

  return (
    <div className="cks">
      <h2 className="cks-title">🩺 Today's check-in <span className="cks-count">{rows.length} in</span></h2>
      <div className="cks-card">
        <div className="cks-metrics">
          {Object.entries(scale).map(([id, s]) => (
            <div key={`s${id}`} className="cks-metric">
              <span className="cks-metric-label">{s.prompt}</span>
              <span className="cks-metric-val">{s.count ? (s.sum / s.count).toFixed(1) : '—'}<small> avg</small></span>
              {s.high.length > 0 && <span className="cks-metric-flag">{s.high.length} at 4+</span>}
            </div>
          ))}
          {Object.entries(yn).map(([id, s]) => (
            <div key={`y${id}`} className="cks-metric">
              <span className="cks-metric-label">{s.prompt}</span>
              <span className={`cks-metric-val ${s.yes.length ? 'warn' : ''}`}>{s.yes.length}<small> yes</small></span>
              {s.no > 0 && <span className="cks-metric-sub">· {s.no} no</span>}
            </div>
          ))}
        </div>

        {flagged.size > 0 && (
          <div className="cks-flags">
            <span className="cks-flags-label">👀 Worth a look:</span> {[...flagged].join(', ')}
          </div>
        )}

        {Object.values(text).some(s => s.items.length > 0) && (
          <div className="cks-text">
            {Object.values(text).map((s, i) => (
              s.items.length > 0 && (
                <div key={i} className="cks-text-block">
                  <span className="cks-text-prompt">{s.prompt}:</span>{' '}
                  {s.items.slice(0, 6).map((it, j) => (
                    <span key={j} className="cks-text-item">{it.name.split(' ')[0]}: <strong>{it.response}</strong></span>
                  ))}
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckinSummary;
